import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Lightbulb,
  ArrowUpFromLine,
  Zap,
  Footprints,
  Crosshair,
  Users,
  RefreshCw,
  BicepsFlexed,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { CoachingTip, CoachingTipCategory } from '@/types/statistics';
import CardHead from './CardHead';

// AI coaching tips for the focus player, rendered as a real deck of cards: the
// active tip on top, the next couple peeking behind it, the rest flown off the
// top once read. Every card is driven purely by its offset from the active
// index, so the cards behind ARE the upcoming tips and the deck genuinely
// empties — at the last card there is nothing left behind it. This replaces the
// old all-expanded stack: ~11 long tips skewed heavily positive was a 700–900px
// wall in which the few actionable "improve" tips got lost. Cards are ordered
// improve-first so the actionable minority leads; the dots are colour-coded by
// level so the row doubles as a glance at the strength/improve skew. Navigation
// is via the chevrons / dots — there is no drag, since this deck is nested
// inside the block carousel (also horizontal) and a deck swipe would fight the
// block swipe. Beta: surfaced straight from the model.
interface ImprovementTipsProps {
  tips: CoachingTip[];
}

const categoryIcons: Record<CoachingTipCategory, React.ElementType> = {
  dead_zone_exposure: Crosshair,
  dead_zone_formation: Users,
  distance_covered: Footprints,
  movement_speed: Zap,
  net_dominance: ArrowUpFromLine,
  net_zone_time: ArrowUpFromLine,
  team_synchronization: RefreshCw,
};

// Highlight numeric values (with optional units) and **bold** spans so the key
// figures pop. Numbers take the section's level tint (green strength / amber
// improve); bold spans get a neutral chip.
function formatMessage(text: string, levelMod: string): React.ReactNode {
  const tokens = text.split(
    /(\*\*[^*]+\*\*|\d+(?:\.\d+)?(?:\s*(?:km\/h|m|s|%))?)/g,
  );
  return tokens.map((token, i) => {
    if (token.startsWith('**') && token.endsWith('**')) {
      return (
        <span key={i} className="tips-bold">
          {token.slice(2, -2)}
        </span>
      );
    }
    if (token !== '' && /^\d+(?:\.\d+)?(?:\s*(?:km\/h|m|s|%))?$/.test(token)) {
      return (
        <span key={i} className={`tips-num ${levelMod}`}>
          {token}
        </span>
      );
    }
    return token;
  });
}

// Improve (warning) cards lead, strengths follow. Array.sort is stable, so the
// model's original order is preserved within each level.
function orderTips(tips: CoachingTip[]): CoachingTip[] {
  const rank = (level: CoachingTip['level']) => (level === 'warning' ? 0 : 1);
  return [...tips].sort((a, b) => rank(a.level) - rank(b.level));
}

// Where a card sits given its offset from the active card (offset = idx -
// active). Negative = already read (flown off the top, gone). 0 = the live
// card. 1–2 = the next tips peeking behind. >=3 = still deep in the deck,
// hidden. Because it's purely offset-driven, the stack thins out near the end
// and is empty behind the final card.
function deckLayer(offset: number) {
  if (offset < 0) return { y: -72, scale: 0.93, opacity: 0, rotateZ: -5 };
  if (offset === 0) return { y: 0, scale: 1, opacity: 1, rotateZ: 0 };
  if (offset === 1) return { y: 12, scale: 0.955, opacity: 1, rotateZ: 0 };
  if (offset === 2) return { y: 23, scale: 0.91, opacity: 1, rotateZ: 0 };
  return { y: 30, scale: 0.88, opacity: 0, rotateZ: 0 };
}

// Read cards fly off ON TOP (above the live card) so the toss is visible; the
// peeking next cards sit below it.
function deckZ(offset: number) {
  return offset < 0 ? 200 + offset : 100 - offset;
}

const ImprovementTips: React.FC<ImprovementTipsProps> = ({ tips }) => {
  const { t, i18n } = useTranslation('report');
  const lang = i18n.language as keyof CoachingTip['message'];
  const ordered = useMemo(() => orderTips(tips), [tips]);
  const total = ordered.length;

  const [index, setIndex] = useState(0);
  const safeIndex = Math.min(index, total - 1);
  const canPrev = safeIndex > 0;
  const canNext = safeIndex < total - 1;

  const paginate = useCallback(
    (dir: number) => setIndex((i) => Math.min(Math.max(i + dir, 0), total - 1)),
    [total],
  );

  if (total === 0) return null;

  return (
    <div className="card tips-card">
      <CardHead
        icon={Lightbulb}
        title={t('tips.title')}
        subtitle={t('tips.subtitle')}
        beta
      />

      <div className="tips-stack">
        {ordered.map((tip, idx) => {
          const offset = idx - safeIndex;
          const isWarning = tip.level === 'warning';
          const mod = isWarning ? 'is-warning' : 'is-positive';
          const message = tip.message[lang] ?? tip.message.en;
          const Icon = categoryIcons[tip.category];
          const isFront = offset === 0;
          return (
            <motion.article
              key={`${tip.category}-${tip.level}-${idx}`}
              className={`tips-face ${mod}`}
              initial={false}
              animate={deckLayer(offset)}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
              style={{
                zIndex: deckZ(offset),
                pointerEvents: isFront ? 'auto' : 'none',
              }}
              aria-hidden={!isFront}
            >
              <div className="tips-face-meta">
                <span className={`tips-face-level ${mod}`}>
                  {isWarning ? (
                    <TrendingUp size={13} strokeWidth={2.2} />
                  ) : (
                    <BicepsFlexed size={13} strokeWidth={2.2} />
                  )}
                  <span>
                    {isWarning ? t('tips.improve') : t('tips.strength')}
                  </span>
                </span>
                <span className="tips-face-cat">
                  {Icon ? <Icon size={13} strokeWidth={2} /> : null}
                  <span>{t(`tips.categories.${tip.category}`)}</span>
                </span>
              </div>
              <p className="tips-face-msg">{formatMessage(message, mod)}</p>
            </motion.article>
          );
        })}
      </div>

      {total > 1 ? (
        <div className="tips-nav">
          <button
            type="button"
            className="tips-arrow"
            onClick={() => paginate(-1)}
            disabled={!canPrev}
            aria-label={t('tips.prevTip')}
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <div className="tips-dots">
            {ordered.map((dotTip, idx) => {
              const dotMod =
                dotTip.level === 'warning' ? 'is-warning' : 'is-positive';
              return (
                <button
                  key={`dot-${idx}`}
                  type="button"
                  className={`tips-dot ${dotMod}${
                    idx === safeIndex ? ' active' : ''
                  }`}
                  aria-label={t('tips.tipPosition', {
                    current: idx + 1,
                    total,
                  })}
                  aria-current={idx === safeIndex ? 'true' : undefined}
                  onClick={() => setIndex(idx)}
                />
              );
            })}
          </div>
          <button
            type="button"
            className="tips-arrow"
            onClick={() => paginate(1)}
            disabled={!canNext}
            aria-label={t('tips.nextTip')}
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ImprovementTips;
